import React, { useContext } from 'react'
import Button from '@3mcom/mds-library/dist/node/Button'
import Link from '@3mcom/mds-library/dist/node/Link'
import { StateContext } from '../../services/StateService'
import { trackPDPData } from '../../services/TealiumService'
import LightboxCTA from './LightboxCTA.jsx'
import scrollService from '../../services/ScrollService.jsx'
import CtaTypeEnum from '../../../common/constants/CtaTypeEnum.js'
import usePDPProductType from '../../hooks/usePDPProductType'
import WTBScreenTip from './WTBScreenTip.jsx'

const BUTTON_STYLES = [
    'mds-button mds-button_primary mds-button_primary--large sps2-pdp_purchase--cta-section-button',
    'mds-button mds-button_secondary mds-button_secondary--large sps2-pdp_purchase--cta-section-button',
    'mds-button mds-button_tertiary mds-button_tertiary--large sps2-pdp_purchase--cta-section-button',
    'mds-button mds-button_tertiary mds-button_tertiary--large sps2-pdp_purchase--cta-section-button'
]

const LIGHTBOX_CTA_TYPES = [
    CtaTypeEnum.CONTACT_ACQUISITION,
    CtaTypeEnum.LEAD_FORM_1,
    CtaTypeEnum.LEAD_FORM_2
]

/**
 * Creates a standardized Tealium tracking object for CTA buttons
 * @param {string} label - The CTA button label
 * @param {string} type - The type of CTA (e.g., 'Button', 'Link')
 * @param {Object} extraProps - Additional properties to merge (e.g., wtb_click)
 * @returns {Object} Tealium tracking object
 */
const createTealiumObj = (label, type, extraProps = {}) => ({
    event_name: `PDP Buying Zone Purchase Options ${type} Click: ${label}`,
    event_type: 'Engagement Event',
    experience_type: 'SNAPS2',
    ...extraProps
})

/**
 * Renders the CTA section with up to 4 prioritized call-to-action buttons
 */
const CTASection = () => {
    const { ctaInfo, richContent, ctaButtons, moreOptions } = useContext(StateContext)
    const { isParentWithHiddenSKUs, isParentWithVisibleSKUs } = usePDPProductType()

    const {
        callToActionLabel: ctaLabel,
        callToActionUrl: ctaUrl,
        productAdvisorLabel,
        productAdvisorLink
    } = ctaInfo || {}

    const { contactAcquisition, postPurchaseForm } = richContent || {}

    // Logic carried over from SNAPS1: when contact acquisition is lightbox,
    // hide the CTA to avoid duplicate labels (DATA ISSUE)
    const showCTALabel = ctaLabel && ctaUrl && !contactAcquisition?.isContactLightbox
    if (!ctaButtons?.length && !productAdvisorLink && !isParentWithHiddenSKUs) {
        return null
    }

    const handleCTAClick = (cta, type = 'Button', scroll = true) => {
        trackPDPData(createTealiumObj(cta.label, type))
        if (!cta?.hasLightbox && scroll) {
            scrollService.scrollToSection(cta.snaps2Id)
        }
    }

    // WTB ScreenTip is primary in isParentWithHiddenSKUs flow, so start all CTAs
    // at secondary style. For isParentWithVisibleSKUs, only demote when WTB is not first.
    const buttonClassIndex =
        isParentWithHiddenSKUs ||
        (isParentWithVisibleSKUs &&
            moreOptions?.options?.length > 0 &&
            ctaButtons?.[0]?.snaps2Id !== CtaTypeEnum.WTB)
            ? 1
            : 0

    // Cap at 3 CTAs when WTB ScreenTip already occupies the primary slot
    const visibleCtaButtons = isParentWithHiddenSKUs
        ? ctaButtons?.slice(0, 3)
        : ctaButtons

    return (
        <div className="sps2-pdp_purchase--cta-section">
            {/* isParentWithHiddenSKUs: WTBScreenTip renders as the primary CTA (index 0) */}
            {isParentWithHiddenSKUs && <WTBScreenTip />}

            {visibleCtaButtons?.map((cta, index) => {
                const buttonClass =
                    BUTTON_STYLES[
                        Math.min(index + buttonClassIndex, BUTTON_STYLES.length - 1)
                    ]

                // Lightbox CTAs (Contact Acquisition, Lead Forms)
                if (LIGHTBOX_CTA_TYPES.includes(cta.snaps2Id)) {
                    return (
                        <LightboxCTA
                            key={`cta-${cta.snaps2Id}`}
                            data-testid={`cta-${cta.snaps2Id}`}
                            cta={cta}
                            buttonClass={buttonClass}
                            onClick={() => handleCTAClick(cta)}
                        />
                    )
                }

                // Service Locator
                if (cta.snaps2Id === CtaTypeEnum.SERVICE_LOCATOR) {
                    return (
                        <a
                            key={`cta-${cta.snaps2Id}`}
                            data-testid={`cta-${cta.snaps2Id}`}
                            className={`cc-sf-button ${buttonClass}`}
                            href="#servicelocator"
                            aria-label={`${cta.label} - Navigate to service locator`}
                        >
                            <span>{cta.label}</span>
                        </a>
                    )
                }

                // Call to Action
                if (cta.snaps2Id === CtaTypeEnum.CALL_TO_ACTION && showCTALabel) {
                    return (
                        <a
                            key={`cta-${cta.snaps2Id}`}
                            data-testid={`cta-${cta.snaps2Id}`}
                            className={buttonClass}
                            href={cta.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => handleCTAClick(cta, 'Link', false)}
                            aria-label={`${cta.label} - Opens in new window`}
                        >
                            <span>{cta.label}</span>
                        </a>
                    )
                }

                // Post-Purchase Form
                if (cta.snaps2Id === CtaTypeEnum.POST_PURCHASE_FORM && postPurchaseForm) {
                    return (
                        <Button
                            key={`cta-${cta.snaps2Id}`}
                            data-testid={`cta-${cta.snaps2Id}`}
                            className={buttonClass}
                            onClick={() => handleCTAClick(cta)}
                            tealiumTracking={true}
                            aria-label={cta.label}
                        >
                            <span>{cta.label}</span>
                        </Button>
                    )
                }

                return null
            })}

            {productAdvisorLink && (
                <Link
                    data-testid="cta-productAdvisorLink"
                    href={productAdvisorLink}
                    className="mds-link mds-link_primary sps2-pdp_purchase--cta-section-productAdvisorLink"
                    aria-label={`${productAdvisorLabel} - Product advisor link`}
                >
                    <span>{productAdvisorLabel}</span>
                </Link>
            )}
        </div>
    )
}

export default CTASection
